/**
 * QualityPage — data-bound Quality tab (Plan 07 Task 7.4).
 *
 * Layout mirrors IA §4.1:
 *   - Row 1: KPI grid with 5 cards (Ratings total, Avg overall,
 *     Avg reactive, Avg brief, Thumbs %).
 *   - Row 2 (2-col): Rating trend `<TrendChart>`-style 3-line chart
 *     (all / reactive / brief) + Rating histogram with prev-period
 *     toggle.
 *   - Row 3: Quality-by-user sortable table with default sort
 *     `avgRating.asc` (worst first — IA §4.1.5 triage intent).
 *
 * bug F3 — the old "Safety refusals" row that sat between the histogram
 * and the quality-by-user table was removed from the frontend. The
 * backend endpoint still exists for future tooling; nothing on this page
 * consumes it anymore.
 *
 * Every widget is wrapped in a <TabErrorBoundary> so one render failure
 * doesn't take down the tab. Each hook owns its own loading / error
 * handling — error states render an inline retry card matching the
 * convention from OverviewPage / EngagementPage.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';

import {
  useQualityByUser,
  useQualityKpis,
  useRatingHistogram,
  useRatingTrend,
} from '@/hooks/use-quality';
import { useOpenUserDetail } from '@/hooks/use-open-user-detail';
import { useRestoreScroll } from '@/hooks/use-restore-scroll';
import { useUrlFilters } from '@/hooks/use-url-filters';
import {
  readTableSort,
  resolveRange,
  type Filters,
  type TableSort,
} from '@/lib/filters-schema';
import { effectiveGranularity } from '@/lib/granularity';
import { formatRating } from '@/lib/format';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { UserLabel } from '@/components/ui/user-label';
import { KPICard } from '@/components/KPICard';
import { RatingDistribution } from '@/components/RatingDistribution';
import { InteractiveChartHint } from '@/components/charts/InteractiveChartHint';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { TraceDrillDownModal } from '@/components/TraceDrillDownModal';
import {
  TOOLTIP_CLASSNAME,
  useChartTooltipTrigger,
} from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
import type {
  QualityByUserRow,
  QualityByUserSortColumn,
  RatingTrendPoint,
} from '@/services/api-types';

// ---------------------------------------------------------------------------
// Inline retry card — mirrors OverviewPage / EngagementPage's ErrorRetry
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          // P3-03 — the bare `underline text-primary` variant had no
          // visible focus style in the dark theme; keyboard users could
          // tab to the Retry affordance without any indication it was
          // focused. Add the same focus-ring token the KPICard info
          // button uses so dark + light themes surface focus consistently.
          className="text-xs underline text-primary hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Avg reactive — the copy is DERIVED from the surface vocabulary, not typed
// ---------------------------------------------------------------------------

/**
 * ONE HUMAN WORD PER COACH SURFACE. The Avg-reactive sentence below is BUILT
 * from this, and that is the whole point of it existing.
 *
 * The number the sentence describes is `avg(rating) filter (where name in
 * COACH_TURN_NAMES)`, and `COACH_TURN_NAMES` is derived from
 * `SURFACE_TRACE_NAME` (`services/analytics/filters.ts`), a constant whose own
 * docstring says it is a constant so that the NEXT surface is one edit. The
 * copy used to name two surfaces as hand-typed literals, and its test matched
 * two hand-typed regexes: add a third surface and the KPI widens silently, the
 * sentence still names two, and the test still passes. That is the same rot the
 * sentence was rewritten to fix, one surface later.
 *
 * So the sentence is no longer written by hand. A new member of the `surface`
 * union is a missing key here and fails to compile — a stronger guard than a
 * test, because it cannot be read past. The union is kept honest against
 * `SURFACE_TRACE_NAME` itself by `tests/dashboard/routes/api/filter-key-parity.test.ts`,
 * which parses every surface the backend knows through this schema.
 */
export const COACH_SURFACE_PROSE: Record<
  Exclude<Filters['surface'], 'all'>,
  string
> = {
  slack: 'Slack (DM/@mention)',
  web: 'Mr. Edge on the web',
};

export const AVG_REACTIVE_TOOLTIP =
  'Mean rating on Mr. Edge\'s reactive turns — messages a user sent, on '
  + new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(
    Object.values(COACH_SURFACE_PROSE),
  )
  + '. Scheduled briefs are not counted.';

// ---------------------------------------------------------------------------
// Quality-by-user helpers
// ---------------------------------------------------------------------------

const QUALITY_BY_USER_TABLE_ID = 'qualityByUser';
/** IA §4.1.5: "sorted by avg rating ASC (worst first)". */
const QUALITY_BY_USER_DEFAULT_SORT: TableSort = {
  column: 'avgRating',
  dir: 'asc',
};
const QUALITY_BY_USER_SORTABLE: ReadonlySet<string> = new Set<
  QualityByUserSortColumn
>(['avgRating', 'worstRating', 'ratedRequests']);

/**
 * Client-only sort column. The backend projection doesn't carry a raw
 * text-feedback count today (only `hasTextFeedbackPct`), so the server-side
 * sort schema rejects it. We sort client-side after the server returns the
 * page and keep `toServerSort` falling back to the default for this column,
 * which still drives the row set the user sees and sorts.
 *
 * Known limitation: pagination + client sort interact — a future "Load more"
 * fetches the next server-sorted page, and the client re-sort applies
 * across whatever's materialized. Matches the current limit=25 default.
 */
const TEXT_FEEDBACK_SORT_COLUMN = 'textFeedbackCount';

/**
 * Map the URL-driven table sort back to the `<col>.<dir>` string the server
 * expects. Falls back to the default when the stored column isn't a
 * server-supported sort column (e.g. client-only `textFeedbackCount`).
 */
function toServerSort(
  stored: TableSort | undefined,
): `${QualityByUserSortColumn}.${'asc' | 'desc'}` {
  const s = stored ?? QUALITY_BY_USER_DEFAULT_SORT;
  const col = QUALITY_BY_USER_SORTABLE.has(s.column)
    ? (s.column as QualityByUserSortColumn)
    : (QUALITY_BY_USER_DEFAULT_SORT.column as QualityByUserSortColumn);
  return `${col}.${s.dir}`;
}

/**
 * Derive the raw text-feedback count for a row. The backend exposes only
 * `hasTextFeedbackPct` today (see QualityByUserRow) so the count is
 * reconstructed the same way the column cell formats it —
 * `round(ratedRequests * pct / 100)`. Null / missing inputs collapse to 0.
 */
function textFeedbackCountOf(row: QualityByUserRow): number {
  const rated = row.ratedRequests ?? 0;
  const pct = row.hasTextFeedbackPct ?? 0;
  if (rated <= 0 || pct <= 0) return 0;
  return Math.round((rated * pct) / 100);
}

/**
 * Client-side comparator for the Text-feedback column.
 *
 * Contract (per task):
 *   - Primary: numeric count from `textFeedbackCountOf`.
 *   - Ties broken by username alphabetical (case-insensitive, stable).
 *   - Null/zero values sort LAST on desc (i.e. first on asc). Policy is
 *     "zeros last on desc": users with no text feedback should not squat
 *     the top of a "who leaves the most feedback" view; on asc they lead
 *     because that's the expected "show me the quiet ones first" direction.
 */
function compareByTextFeedback(
  a: QualityByUserRow,
  b: QualityByUserRow,
  dir: 'asc' | 'desc',
): number {
  const ca = textFeedbackCountOf(a);
  const cb = textFeedbackCountOf(b);

  // Zeros-last-on-desc: push zero counts to the bottom when sorting
  // descending. On asc, zeros fall naturally to the top via numeric order,
  // which matches the "quiet users first" reading of an ascending sort.
  if (dir === 'desc') {
    if (ca === 0 && cb !== 0) return 1;
    if (cb === 0 && ca !== 0) return -1;
  }

  if (ca !== cb) return dir === 'desc' ? cb - ca : ca - cb;

  // Tie-breaker: username alphabetical, case-insensitive, stable across
  // both directions so the secondary order never flips with the primary.
  const ua = (a.userId ?? '').toLowerCase();
  const ub = (b.userId ?? '').toLowerCase();
  if (ua < ub) return -1;
  if (ua > ub) return 1;
  return 0;
}

/**
 * Role-badge renderer — matches the Engagement tab's role column.
 *
 * K-04 (Phase 7): backend emits binary `'rm' | 'non-rm'` only (see
 * queries.ts:2303). NULL/unknown collapses to 'non-rm' at the mapper level.
 */
function RoleBadge({
  role,
}: {
  role: QualityByUserRow['role'];
}): React.ReactElement {
  if (role === 'rm') {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600/80">
        RM
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="bg-blue-100 text-blue-900">
      Non-RM
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Chart helpers — IA §4.1.2 rating trend
// ---------------------------------------------------------------------------

// P12 — delegate to central formatter so hourly buckets include day+time.
import { formatBucketLabel as formatBucketLabelShared } from '@/lib/format-bucket';
function formatBucketLabel(bucket: string): string {
  return formatBucketLabelShared(bucket, 'auto');
}

/**
 * Series colour tokens used by the rating trend. We reach through the shadcn
 * HSL variables exposed in `index.css` so the series automatically re-skins
 * when a theme swaps `--primary` / `--foreground`. Kept as constants so the
 * legend colour dots and the recharts `<Line stroke>` stay in lock-step.
 */
const RATING_TREND_COLOURS = {
  all: 'hsl(var(--primary))',
  reactive: 'hsl(var(--chart-2, 142 71% 45%))',
  brief: 'hsl(var(--chart-3, 271 76% 53%))',
} as const;

type RatingTrendSeriesKey = 'all' | 'reactive' | 'brief';

const RATING_TREND_SERIES: ReadonlyArray<{
  key: RatingTrendSeriesKey;
  label: string;
}> = [
  { key: 'all', label: 'All' },
  { key: 'reactive', label: 'Reactive' },
  { key: 'brief', label: 'Brief' },
];

/**
 * Themed tooltip for the rating-trend chart (bug G1 + G2).
 *
 * Replaces recharts' default tooltip (white-on-white in dark themes, raw
 * 4.12345 float precision) with a shadcn-token-backed popover that renders
 * `<avg>★ (<count> feedbacks)` per series. Counts are read from the
 * backend's `counts: { all, reactive, brief }` payload already exposed on
 * `RatingTrendPoint` (services/api-types.ts) — no backend change required.
 */
function RatingTrendTooltip({
  active,
  payload,
  label,
  hasBrief = true,
}: TooltipProps<number, string> & {
  /**
   * P2-01 — when the window contains zero rated briefs, suppress the Brief
   * row entirely so the tooltip matches the caption ("No rated scheduled
   * briefs…") instead of claiming a flat `— (0 feedbacks)` Brief line.
   */
  hasBrief?: boolean;
}): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  // The full row (one RatingTrendPoint) is attached to every series entry
  // as `payload.payload` — we pick it from the first one to read counts.
  const row = (payload[0]?.payload ?? null) as RatingTrendPoint | null;

  return (
    <div data-testid="rating-trend-tooltip" className={TOOLTIP_CLASSNAME}>
      <div className="mb-1 font-medium">
        {typeof label === 'string' ? formatBucketLabel(label) : ''}
      </div>
      <ul className="space-y-0.5">
        {RATING_TREND_SERIES.map(({ key, label: seriesLabel }) => {
          // P2-01: skip Brief row entirely when the window has no rated
          // briefs. All + Reactive stay unconditional.
          if (key === 'brief' && !hasBrief) return null;
          const entry = payload.find((p) => p.dataKey === key);
          if (!entry) return null;
          const raw = entry.value;
          const value =
            typeof raw === 'number' && Number.isFinite(raw)
              ? `${raw.toFixed(1)}★`
              : '—';
          const count = row?.counts?.[key] ?? 0;
          return (
            <li key={key} className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-sm"
                style={{ background: RATING_TREND_COLOURS[key] }}
              />
              <span className="min-w-[60px] text-muted-foreground">
                {seriesLabel}:
              </span>
              <span className="font-medium tabular-nums">{value}</span>
              <span className="text-muted-foreground">
                ({count} {count === 1 ? 'feedback' : 'feedbacks'})
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RatingTrendChart({
  data,
  loading,
  height = 280,
}: {
  data: RatingTrendPoint[];
  loading: boolean;
  height?: number;
}): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  if (loading) {
    return (
      <div
        data-testid="rating-trend-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="rating-trend-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No rated traces in selected period
      </div>
    );
  }
  // bug F6 — defensive row normalization.
  //
  // The backend always emits `{ all, reactive, brief: number | null,
  // counts: { ... } }`, but some historical payloads (and future api-types
  // consumers) may leave `brief` as `undefined` if no scheduled_brief was
  // rated in the window. Normalize every series to `number | null` so
  // recharts plots a flat gap instead of silently dropping the series,
  // and keep the `counts` object present so the tooltip reader never
  // hits `undefined.counts`.
  const rows = data.map((r) => ({
    bucket: r.bucket,
    all: r.all ?? null,
    reactive: r.reactive ?? null,
    brief: r.brief ?? null,
    counts: {
      all: r.counts?.all ?? 0,
      reactive: r.counts?.reactive ?? 0,
      brief: r.counts?.brief ?? 0,
    },
  }));
  const hasBrief = rows.some((r) => (r.counts?.brief ?? 0) > 0);
  return (
    <div
      data-testid="rating-trend"
      data-has-brief={hasBrief ? 'true' : 'false'}
      style={{ width: '100%', height }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={rows}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v)}
          />
          <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} />
          <Tooltip
            {...tooltipTrigger}
            // P2-01: thread the computed `hasBrief` (whole-window check)
            // into the tooltip so the Brief row is suppressed when no
            // rated briefs exist — matching the caption + totals badge.
            content={(props) => (
              <RatingTrendTooltip
                {...(props as TooltipProps<number, string>)}
                hasBrief={hasBrief}
              />
            )}
          />
          <Legend />
          <Line
            dataKey="all"
            type="monotone"
            stroke={RATING_TREND_COLOURS.all}
            strokeWidth={3}
            name="All"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            dataKey="reactive"
            type="monotone"
            stroke={RATING_TREND_COLOURS.reactive}
            strokeWidth={2}
            name="Reactive"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          {/* bug F6 — `<Line>` for the Brief series always mounts so the
              legend stays stable across filter changes even when the
              window contains zero delivered briefs. `connectNulls` keeps
              a single rated brief visible as a point even when the
              neighbours are null. */}
          <Line
            dataKey="brief"
            type="monotone"
            stroke={RATING_TREND_COLOURS.brief}
            strokeWidth={2}
            name="Brief"
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      {!hasBrief && (
        <div
          data-testid="rating-trend-no-brief"
          className="mt-1 text-[11px] text-muted-foreground"
        >
          No rated scheduled briefs in the selected window — Brief series
          is flat.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function QualityPage(): React.ReactElement {
  const { filters } = useUrlFilters();
  const { openUserDetail } = useOpenUserDetail();
  // L6 — restore scroll on return from the user drill-down.
  useRestoreScroll();

  // Effective granularity — kept for future label wiring / parity with
  // Overview/Engagement. The server's auto-rule still rules data shape.
  const range = resolveRange(filters);
  const _eff = effectiveGranularity(filters.granularity, range.from, range.to);
  void _eff;

  // P5-01 — future-dated ranges land here with all KPIs nulled out and every
  // chart empty, which reads as "is the dashboard broken?" rather than
  // "you picked tomorrow." Detect `range.from > today` at the page shell so
  // the rest of the layout can short-circuit to an explicit empty state.
  const todayIso = new Date().toISOString().slice(0, 10);
  const isFutureRange = range.from > todayIso;

  // Prev-period toggle for the histogram (IA §4.1.3).
  const [includePrev, setIncludePrev] = React.useState<boolean>(false);

  // v1.4 dashboard-ux-fix: Rating-bar drill-down state. `null` keeps the
  // modal closed AND its hook inert (enabled: false), so no stray fetch
  // fires on first paint. Re-clicking the same bar is handled by the
  // RatingDistribution component — it always calls onBarClick with the
  // rating label, so toggling off happens at this layer if/when needed.
  const [drillDownRating, setDrillDownRating] = React.useState<
    1 | 2 | 3 | 4 | 5 | null
  >(null);

  const kpis = useQualityKpis(filters);
  const trend = useRatingTrend(filters, filters.granularity);
  const histogram = useRatingHistogram(filters, { includePrev });

  const byUserSort = readTableSort(filters, QUALITY_BY_USER_TABLE_ID);
  const byUser = useQualityByUser(filters, {
    limit: 25,
    sort: toServerSort(byUserSort),
    minRatings: 3,
  });

  const k = kpis.data;

  // Thumbs % is already scaled to 0..100 by the backend. KPICard's
  // `'percent'` format appends `%` directly so we don't need a subLabel
  // workaround — the star suffix from `'rating'` would be wrong semantically.
  const thumbsValue =
    k?.thumbsPct.current === null || k?.thumbsPct.current === undefined
      ? null
      : Number(k.thumbsPct.current.toFixed(1));

  // P2-04 — sample-size derivation for the Avg brief / Avg reactive KPIs.
  //
  // `QualityKpis` exposes only `{current, previous, delta}` per metric — the
  // backend doesn't currently emit a per-slice rating-count with the KPI
  // response. The rating-trend endpoint does (RatingTrendPoint.counts), and
  // it's scoped to the same filter window, so we sum its per-bucket
  // `brief` / `reactive` counts to drive PeriodCompareDelta's small-N mute
  // (pill goes grayscale + `n=N` badge when < 3). When the trend hasn't
  // loaded yet we pass `undefined` so the delta pill renders normally
  // instead of falsely muting on a transient 0.
  const trendRows = trend.data;
  const briefSampleSize = trendRows
    ? trendRows.reduce((s, r) => s + (r.counts?.brief ?? 0), 0)
    : undefined;
  const reactiveSampleSize = trendRows
    ? trendRows.reduce((s, r) => s + (r.counts?.reactive ?? 0), 0)
    : undefined;

  // Quality-by-user columns — only the server-supported ones are sortable.
  const columns: Array<SortableColumn<QualityByUserRow>> = React.useMemo(
    () => [
      {
        key: 'realName',
        header: 'Name',
        format: (_v, row) => (
          <UserLabel
            name={row.realName}
            username={row.userId}
            variant="cell"
          />
        ),
      },
      {
        key: 'userId',
        header: 'User',
        format: (v) => `@${String(v ?? '')}`,
      },
      {
        key: 'role',
        header: 'Role',
        format: (_v, row) => <RoleBadge role={row.role} />,
      },
      {
        key: 'ratedRequests',
        header: 'Rated requests',
        sortable: true,
        align: 'right',
      },
      {
        key: 'avgRating',
        header: 'Avg rating',
        sortable: true,
        align: 'right',
        format: (v) =>
          v === null || v === undefined ? '—' : `${(v as number).toFixed(1)}★`,
      },
      {
        key: 'worstRating',
        header: 'Worst rating',
        sortable: true,
        align: 'right',
        format: (v) => formatRating(v as number | null),
      },
      {
        key: 'hasTextFeedbackPct',
        header: 'Has text %',
        align: 'right',
        format: (v) =>
          v === null || v === undefined
            ? '—'
            : `${(v as number).toFixed(1)}%`,
      },
      {
        // bug F5 — new "Text feedback" column. The backend QualityByUserRow
        // doesn't expose a raw count yet, so we derive it from the same
        // fields the % column already uses (rated * pct / 100). Rounded to
        // the nearest whole comment to match the "Ratings w/ text" KPI
        // rounding convention on Overview.
        //
        // Sortable client-side only — the server sort schema (/by-user)
        // doesn't accept this column, so we sort the returned page below
        // via `compareByTextFeedback`. First click defaults to desc
        // (numeric convention in SortableTable: align=right → firstDir
        // desc) so users surface the most-feedback authors first.
        key: 'textFeedbackCount',
        header: 'Text feedback',
        sortable: true,
        align: 'right',
        format: (_v, row) => {
          const count = textFeedbackCountOf(row);
          return <span className="tabular-nums">{count}</span>;
        },
      },
    ],
    [],
  );

  return (
    <section aria-labelledby="quality-heading" className="space-y-6">
      <header>
        <h1 id="quality-heading" className="text-2xl font-semibold">
          Quality
        </h1>
        <p className="text-sm text-muted-foreground">
          Are users happy — ratings, reactive vs brief splits, and triage list.
        </p>
        <RecountNotice queries={[kpis, trend, histogram, byUser]} />
      </header>

      {/* P5-01 — when the resolved window starts in the future every widget
          empties out, which is indistinguishable from "dashboard broken."
          Call it out up front so the filter is the obvious suspect. */}
      {isFutureRange && (
        <Card data-testid="quality-future-range-banner">
          <CardContent className="p-4 text-sm text-muted-foreground">
            Selected date range starts in the future ({range.from} →{' '}
            {range.to}). Pick a recent preset or past custom range to see
            data.
          </CardContent>
        </Card>
      )}

      {/* Row 1 — KPI grid (IA §4.1.1). 5 cards on desktop. */}
      <TabErrorBoundary name="KPI row">
        {kpis.isError ? (
          <ErrorRetry label="KPIs" onRetry={() => void kpis.refetch()} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-5 gap-3 md:gap-4">
            <KPICard
              label="Ratings"
              value={k?.ratings.current ?? null}
              delta={k?.ratings.delta ?? null}
              subLabel={
                k
                  ? `${k.ratings.withText} w/ text`
                  : undefined
              }
              loading={kpis.isLoading}
              tooltip="Count of rated traces (reactive + brief) in the window. 'w/ text' = reactions with a free-form comment."
            />
            <KPICard
              label="Avg rating"
              value={k?.avgOverall.current ?? null}
              format="rating"
              delta={k?.avgOverall.delta ?? null}
              loading={kpis.isLoading}
              tooltip="Mean star rating across all rated traces in the window (1–5)."
            />
            <KPICard
              label="Avg reactive"
              value={k?.avgReactive.current ?? null}
              format="rating"
              delta={k?.avgReactive.delta ?? null}
              // P2-04 — surface reactive sample size so a big delta on
              // n<3 ratings is visibly muted (gray pill + n=N badge).
              // `rating` threshold is 3, not the default 10 (counts).
              deltaCurrent={k?.avgReactive.current ?? null}
              deltaPrevious={k?.avgReactive.previous ?? null}
              sampleSize={reactiveSampleSize}
              minPreviousSampleForDelta={3}
              loading={kpis.isLoading}
              // The number behind this is `avg(rating) filter (where name in
              // COACH_TURN_NAMES)`. The sentence is generated from the surface
              // vocabulary rather than typed — see COACH_SURFACE_PROSE.
              tooltip={AVG_REACTIVE_TOOLTIP}
            />
            <KPICard
              label="Avg brief"
              value={k?.avgBrief.current ?? null}
              format="rating"
              delta={k?.avgBrief.delta ?? null}
              // P2-04 — same small-N guard for Avg brief. On a 24h window
              // with 2 rated briefs a +35% delta is noise, not signal.
              deltaCurrent={k?.avgBrief.current ?? null}
              deltaPrevious={k?.avgBrief.previous ?? null}
              sampleSize={briefSampleSize}
              minPreviousSampleForDelta={3}
              loading={kpis.isLoading}
              tooltip="Mean rating on scheduled_brief traces (daily and weekly morning briefs). Manual /brief-command briefs are not counted here."
            />
            <KPICard
              label="Thumbs up"
              value={thumbsValue}
              format="percent"
              delta={k?.thumbsPct.delta ?? null}
              subLabel={thumbsValue === null ? undefined : '≥ 4★ of rated traces'}
              loading={kpis.isLoading}
              tooltip="% of rated traces with rating ≥ 4 stars."
              // P2-06 — row 1 has 5 cards which lays out as 2/2/1 on the
              // mobile 2-col grid, leaving the 5th card orphaned in a
              // visually awkward half-row. Span the 5th across both
              // columns below the xl breakpoint so the mobile row finishes
              // full-width. At xl+ the 5-col layout already fills cleanly
              // and we restore `col-span-1`.
              className="col-span-2 xl:col-span-1"
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* Row 2 — Rating trend (2/3) + Rating histogram w/ prev toggle (1/3).
          IA §4.1.2 / §4.1.3. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <TabErrorBoundary name="Rating trend">
            {trend.isError ? (
              <ErrorRetry
                label="Rating trend"
                onRetry={() => void trend.refetch()}
              />
            ) : (
              <Card>
                <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Rating trend (all / reactive / brief)
                  </CardTitle>
                  {/* bug-C-totals: the series themselves are averages, so
                      summing the line values is meaningless. Instead we sum
                      the per-bucket `counts.{all,reactive,brief}` payload
                      exposed by RatingTrendPoint and label the entries as
                      feedback counts — which is the actionable "how many
                      ratings contributed to this line" number. */}
                  <TimelineTotalsBadge
                    data-testid="rating-trend-totals"
                    loading={trend.isLoading}
                    items={(() => {
                      const rows = trend.data ?? [];
                      const sumCounts = (key: 'all' | 'reactive' | 'brief') =>
                        rows.reduce(
                          (s, r) => s + (r.counts?.[key] ?? 0),
                          0,
                        );
                      const loading = trend.isLoading;
                      // P2-02: mirror the chart's caption logic — when no
                      // bucket contains rated briefs, drop the Brief entry
                      // entirely instead of parading a stale 0. All +
                      // Reactive stay unconditional.
                      const hasBrief = sumCounts('brief') > 0;
                      const items: TimelineTotalItem[] = [
                        {
                          key: 'all',
                          label: 'All',
                          color: 'hsl(var(--primary))',
                          value: sumCounts('all'),
                        },
                        {
                          key: 'reactive',
                          label: 'Reactive',
                          color: 'hsl(var(--chart-2, 142 71% 45%))',
                          value: sumCounts('reactive'),
                        },
                      ];
                      if (loading || hasBrief) {
                        items.push({
                          key: 'brief',
                          label: 'Brief',
                          color: 'hsl(var(--chart-3, 271 76% 53%))',
                          value: sumCounts('brief'),
                        });
                      }
                      return items;
                    })()}
                  />
                </CardHeader>
                <CardContent className="p-4">
                  <RatingTrendChart
                    data={trend.data ?? []}
                    loading={trend.isLoading}
                  />
                </CardContent>
              </Card>
            )}
          </TabErrorBoundary>
        </div>
        <TabErrorBoundary name="Rating histogram">
          {histogram.isError ? (
            <ErrorRetry
              label="Rating histogram"
              onRetry={() => void histogram.refetch()}
            />
          ) : (
            <Card>
              <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-2 gap-y-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Rating distribution
                  </CardTitle>
                  <InteractiveChartHint data-testid="rating-histogram-hint" />
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox
                    data-testid="rating-histogram-prev-toggle"
                    // P3-02 — screen readers announce only "checkbox" before
                    // reading the sibling label, so blind users hear the
                    // control without knowing what it toggles until the
                    // label fires. Explicit aria-label makes the affordance
                    // self-describing even when the label text scrolls out
                    // of the reading position.
                    aria-label="Toggle previous-period overlay"
                    checked={includePrev}
                    onCheckedChange={(v) => setIncludePrev(v === true)}
                  />
                  vs prev period
                </label>
              </CardHeader>
              <CardContent className="p-4">
                {/* TODO(quality P3-01): the "TOTAL: N" badge inside the
                    shared <RatingDistribution> overlaps the top Y-axis
                    tick on narrow widths (mobile-02-loaded.png). Fix
                    lives in src/components/RatingDistribution.tsx —
                    shared component, out of scope for this page. */}
                <RatingDistribution
                  buckets={histogram.data?.current ?? []}
                  previous={
                    includePrev ? histogram.data?.previous : undefined
                  }
                  loading={histogram.isLoading}
                  onBarClick={(rating) => setDrillDownRating(rating)}
                />
              </CardContent>
            </Card>
          )}
        </TabErrorBoundary>
      </div>

      {/* Row 3 — Quality by user sortable table (worst first). IA §4.1.5.
          bug F3 — the old Safety refusals row (previously Row 3) was
          removed from the frontend; backend endpoint stays intact. */}
      <TabErrorBoundary name="Quality by user">
        {byUser.isError ? (
          <ErrorRetry
            label="Quality by user"
            onRetry={() => void byUser.refetch()}
          />
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <div className="text-sm font-medium text-muted-foreground">
                Quality by user
              </div>
              {/* P2-05 — the table defaults to `avgRating asc` (worst first)
                  so the triage list surfaces the users at the bottom of the
                  scale, but the only visual hint is the ▲ glyph on the
                  column header. Readers coming from Overview don't know
                  the intent until they notice the sort arrow. Surface the
                  intent as an inline caption while the default sort is
                  active; hide when the user re-sorts so the caption
                  doesn't lie. Style matches the chart-caption convention
                  used under the rating-trend ("No rated scheduled briefs…"). */}
              {(() => {
                const effSort = byUserSort ?? QUALITY_BY_USER_DEFAULT_SORT;
                const isDefault =
                  effSort.column === QUALITY_BY_USER_DEFAULT_SORT.column &&
                  effSort.dir === QUALITY_BY_USER_DEFAULT_SORT.dir;
                if (!isDefault) return null;
                return (
                  <span
                    data-testid="quality-by-user-sort-caption"
                    className="text-[11px] text-muted-foreground"
                  >
                    Sorted by average rating, worst first
                  </span>
                );
              })()}
            </div>
            <SortableTable<QualityByUserRow>
              tableId={QUALITY_BY_USER_TABLE_ID}
              columns={columns}
              // Client-side sort for the `textFeedbackCount` column —
              // the server sort schema only supports avgRating / worstRating
              // / ratedRequests, so when the user picks Text feedback we
              // re-order the page locally. All other columns hit the
              // server-sorted path (rows come back pre-sorted by the API).
              rows={(() => {
                const raw = byUser.data ?? [];
                if (byUserSort?.column !== TEXT_FEEDBACK_SORT_COLUMN)
                  return raw;
                const dir = byUserSort.dir;
                return [...raw].sort((a, b) =>
                  compareByTextFeedback(a, b, dir),
                );
              })()}
              getRowKey={(r) => r.userId}
              defaultSort={QUALITY_BY_USER_DEFAULT_SORT}
              onRowClick={(row) => {
                if (row.userId) openUserDetail(row.userId);
              }}
              isLoading={byUser.isLoading}
              emptyMessage="No rated users in selected period"
              mobileCardRender={(row) => {
                const rated = row.ratedRequests ?? 0;
                const pct = row.hasTextFeedbackPct ?? 0;
                const textCount = Math.round((rated * pct) / 100);
                return (
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <UserLabel
                          name={row.realName}
                          username={row.userId}
                          variant="stacked"
                          className="min-w-0"
                        />
                        <RoleBadge role={row.role} />
                      </div>
                      <span
                        aria-hidden
                        className="text-muted-foreground shrink-0 text-lg leading-none"
                      >
                        ›
                      </span>
                    </div>
                    <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 text-sm">
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Rated
                        </dt>
                        <dd className="font-medium tabular-nums">{rated}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Avg
                        </dt>
                        <dd className="font-medium tabular-nums">
                          {row.avgRating === null ||
                          row.avgRating === undefined
                            ? '—'
                            : `${row.avgRating.toFixed(1)}★`}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          Worst
                        </dt>
                        <dd className="font-medium tabular-nums">
                          {formatRating(row.worstRating)}
                        </dd>
                      </div>
                    </dl>
                    <div className="text-xs text-muted-foreground">
                      Text feedback: {textCount} ({pct.toFixed(0)}%)
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* v1.4 dashboard-ux-fix: rating-bar drill-down. Inert (enabled: false)
          when `drillDownRating === null`. */}
      <TraceDrillDownModal
        open={drillDownRating !== null}
        onClose={() => setDrillDownRating(null)}
        filters={filters}
        extras={drillDownRating !== null ? { rating: drillDownRating } : {}}
        title={
          drillDownRating !== null
            ? `Traces rated ${drillDownRating}★`
            : ''
        }
        subtitle="Every trace at this rating for the current filters."
        testIdPrefix="quality-rating-drilldown"
      />
    </section>
  );
}
